import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import MuiListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import useMenu from "hooks/useMenu";
import useUser from "hooks/useUser";
import RequestFormModal from "components/RequestFormModal/RequestFormModal";
import useModal from "hooks/useModal";
import { SHOW_SUBMIT_REQUEST } from "services/Http";
import MaterialSymbol from "components/MaterialSymbol/MaterialSymbol";
import { isAdminUiSkinEnabled } from "adminUiSkin";
import { installMenuDebugHelpers, logMenuDebug } from "utils/menuDebug";

const showSubmitRequest = SHOW_SUBMIT_REQUEST === "Y";

const useStyles = makeStyles((theme) => ({
    root: (props) => ({
        width: "100%",
        maxWidth: props.isShadcn ? "none" : 360,
        backgroundColor: "#333333",
        boxSizing: "border-box",
    }),
    white: {
        color: theme.palette.common.white,
    },
    nested: {
        paddingLeft: theme.spacing(4),
    },
}));

const ListItem = withStyles({
    root: {
        borderRadius: 5,
        margin: "5px 0px 5px 0px",
        "& span": {
            fontSize: "14px",
        },
        "&$selected": {
            backgroundColor: "#ef662f",
            color: "white",
            "& .MuiListItemIcon-root": {
                color: "white",
            },
        },
        "&$selected:hover": {
            backgroundColor: "#ef662f",
            color: "white",
            "& .MuiListItemIcon-root": {
                color: "white",
            },
        },
        "&:hover": {
            backgroundColor: "#474747",
            color: "white",
            "& .MuiListItemIcon-root": {
                color: "white",
            },
        },
    },
    selected: {},
})(MuiListItem);

function SidebarItemLabel({ name }) {
    const textRef = React.useRef(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useEffect(() => {
        const node = textRef.current;
        if (!node) return;

        const checkTruncation = () => {
            setIsTruncated(node.scrollWidth > node.clientWidth);
        };

        checkTruncation();
        window.addEventListener("resize", checkTruncation);

        return () => {
            window.removeEventListener("resize", checkTruncation);
        };
    }, [name]);

    return (
        <Tooltip title={name} placement="right" arrow disableHoverListener={!isTruncated}>
            <ListItemText
                primary={name}
                primaryTypographyProps={{
                    ref: textRef,
                }}
            />
        </Tooltip>
    );
}

function CustomListItem(props) {
    const {
        classes,
        name,
        views = [],
        layout,
        path,
        createLinks = () => {},
        history,
        depth = 0,
        userEmail = "",
    } = props;

    const hasChildren = views.length > 0;
    const canNavigate = Boolean(layout && path);
    const targetPath = canNavigate ? `${layout}${path}` : null;

    const handleClick = () => {
        logMenuDebug("menu-item-click", {
            name,
            depth,
            hasChildren,
            canNavigate,
            layout,
            path,
            targetPath,
            currentPath: history.location.pathname,
            userEmail,
        });

        if (canNavigate && history.location.pathname !== targetPath) {
            history.push(targetPath);

            window.setTimeout(() => {
                logMenuDebug("menu-item-post-push", {
                    name,
                    targetPath,
                    resultingPath: window.location.pathname,
                    userEmail,
                });
            }, 0);
            return;
        }

        logMenuDebug("menu-item-no-navigation", {
            name,
            depth,
            hasChildren,
            canNavigate,
            layout,
            path,
            currentPath: history.location.pathname,
            userEmail,
        });
    };

    const isSelected = Boolean(targetPath) && history.location.pathname === targetPath;

    return (
        <>
            <ListItem
                button={canNavigate}
                onClick={handleClick}
                className={`custom-sidebar-item custom-sidebar-item-depth-${depth} ${canNavigate ? "custom-sidebar-item-link" : "custom-sidebar-item-group"}`}
                data-menu-name={name}
                data-menu-depth={depth}
                data-menu-has-children={hasChildren ? "true" : "false"}
                data-menu-can-navigate={canNavigate ? "true" : "false"}
                data-menu-path={targetPath || ""}
                selected={isSelected}
            >
                {/*<ListItemIcon style={{ minWidth: 35 }}>
                    <props.icon className={classes.white} />
                </ListItemIcon>*/}
                <SidebarItemLabel name={name} />
            </ListItem>
            {views.length > 0 ? (
                <Collapse
                    /*
                     * Temporary production workaround (2026-08-21):
                     * keep all submenu sections permanently expanded because
                     * collapsible menu groups were intermittently becoming
                     * non-responsive for some users in production.
                     *
                     * If/when we restore the old behavior, this block should go
                     * back to a local `open` state toggle and the parent item
                     * click should reopen/close children again.
                     *
                     * Reference:
                     * - file to revisit: src/components/CustomSidebar/CustomSidebar.js
                     * - original pre-workaround file can be inspected from git HEAD
                     *   with:
                     *   `git show HEAD:V2/frontend/Dashboard/src/components/CustomSidebar/CustomSidebar.js`
                     * - compare current changes with:
                     *   `git diff -- src/components/CustomSidebar/CustomSidebar.js`
                     * - to restore collapsible behavior, recover the previous
                     *   local `open` state, chevron rendering, `in={open}`, and
                     *   `unmountOnExit` from the HEAD version above.
                     */
                    in={true}
                    timeout="auto"
                    className={`custom-sidebar-collapse custom-sidebar-collapse-depth-${depth}`}
                >
                    <List component="div" disablePadding>
                        {createLinks(views, depth + 1)}
                    </List>
                </Collapse>
            ) : null}
        </>
    );
}

const ASSIGNED_ROLES_ONLY = true;

export default function CustomSidebar() {
    const isShadcn = isAdminUiSkinEnabled();
    const classes = useStyles({ isShadcn });
    const history = useHistory();
    const [user] = useUser();
    const { treeMenu = [] } = useMenu(ASSIGNED_ROLES_ONLY);
    const { open, handleOpenModal, handleCloseModal } = useModal();

    React.useEffect(() => {
        installMenuDebugHelpers();
    }, []);

    React.useEffect(() => {
        logMenuDebug("sidebar-mounted", {
            userEmail: user?.email || user?.userName || "",
            menuCount: treeMenu.length,
            skin: document.documentElement.getAttribute("data-admin-ui-skin") || "",
        });
    }, [treeMenu.length, user]);

    React.useEffect(() => {
        const unlisten = history.listen((location, action) => {
            logMenuDebug("route-change", {
                action,
                pathname: location.pathname,
                search: location.search,
                userEmail: user?.email || user?.userName || "",
            });
        });

        return () => {
            unlisten();
        };
    }, [history, user]);

    function createLinks(menu = [], depth = 0) {
        return menu.map((submenu, index) => (
            <CustomListItem
                key={index}
                index={index}
                classes={classes}
                icon={submenu.icon}
                name={submenu.name}
                views={submenu.views}
                layout={submenu.layout}
                path={submenu.path}
                createLinks={createLinks}
                history={history}
                depth={depth}
                userEmail={user?.email || user?.userName || ""}
            />
        ));
    }

    return (
        <div
            className={`${classes.root} custom-sidebar-root`}
            style={isShadcn ? undefined : { paddingLeft: 10, paddingRight: 10 }}
        >
            <List component="nav">
                {createLinks(treeMenu)}
                <RequestFormModal open={open} handleCloseModal={handleCloseModal} />
                {user && user.isAdmin === 0 && showSubmitRequest && (
                    <ListItem
                        button
                        onClick={handleOpenModal}
                        style={{ paddingLeft: 10 }}
                        selected={false}
                    >
                        <ListItemIcon style={{ minWidth: 35 }}>
                            <MaterialSymbol style={{ color: "white" }}>send</MaterialSymbol>
                        </ListItemIcon>
                        <ListItemText primary={"Submit Request"} />
                    </ListItem>
                )}
            </List>
        </div>
    );
}
